import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import fr from "date-fns/locale/fr";
import "react-big-calendar/lib/css/react-big-calendar.css";

import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import api from "services/api";
import { useAuth } from "../../context/AuthContext";

const locales = { fr };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date) => startOfWeek(date, { weekStartsOn: 1 }),
  getDay,
  locales,
});

const messagesFr = {
  allDay: "Journée",
  previous: "Précédent",
  next: "Suivant",
  today: "Aujourd'hui",
  month: "Mois",
  week: "Semaine",
  day: "Jour",
  agenda: "Agenda",
  date: "Date",
  time: "Heure",
  event: "Réunion",
  noEventsInRange: "Aucune réunion sur cette période",
  showMore: (n) => `+ ${n} autres`,
};

function statutBadge(statut) {
  const map = {
    planifiee: { bg: "#e3f2fd", color: "#1565c0", label: "Planifiée" },
    terminee:  { bg: "#e8f5e9", color: "#2e7d32", label: "Terminée" },
    annulee:   { bg: "#ffebee", color: "#c62828", label: "Annulée" },
  };
  const cfg = map[statut] || map.planifiee;
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, borderRadius: 12,
      padding: "3px 10px", fontSize: 11, fontWeight: 700,
    }}>{cfg.label}</span>
  );
}

export default function Reunions() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [reunions, setReunions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("liste");

  useEffect(() => {
    api.get("/reunions").then(({ data }) => {
      setReunions(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const events = reunions.map((r) => {
    const [h, m] = (r.heure_debut || "00:00").split(":");
    const [hf, mf] = (r.heure_fin || r.heure_debut || "00:00").split(":");
    const start = new Date(r.date);
    start.setHours(parseInt(h, 10), parseInt(m, 10), 0);
    const end = new Date(r.date);
    end.setHours(parseInt(hf, 10), parseInt(mf, 10), 0);
    return { id: r.id, title: r.titre, start, end, resource: r };
  });

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} md={8}>
            <MDTypography variant="h4" fontWeight="bold" color="dark">
              Réunions des enseignants
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {reunions.length} réunion{reunions.length > 1 ? "s" : ""} enregistrée{reunions.length > 1 ? "s" : ""}
            </MDTypography>
          </Grid>
          <Grid item xs={12} md={4} sx={{ textAlign: { md: "right" } }}>
            {isAdmin && (
            <MDButton onClick={() => navigate("/reunions/nouveau")}
              sx={{ background: "linear-gradient(135deg, #1565c0, #1976d2)", color: "#fff", fontWeight: 700 }}>
              <Icon sx={{ mr: 1 }}>add</Icon> Nouvelle réunion
            </MDButton>
            )}
          </Grid>
        </Grid>

        {/* Toggle Liste / Calendrier */}
        <MDBox display="flex" gap={1} mb={2}>
          <MDBox onClick={() => setViewMode("liste")} sx={{
            cursor: "pointer", px: 2, py: 1, borderRadius: "8px",
            background: viewMode === "liste" ? "#1565c0" : "#f5f5f5",
            color: viewMode === "liste" ? "#fff" : "#757575",
            fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5,
          }}>
            <Icon fontSize="small">view_list</Icon> Liste
          </MDBox>
          <MDBox onClick={() => setViewMode("calendrier")} sx={{
            cursor: "pointer", px: 2, py: 1, borderRadius: "8px",
            background: viewMode === "calendrier" ? "#1565c0" : "#f5f5f5",
            color: viewMode === "calendrier" ? "#fff" : "#757575",
            fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", gap: 0.5,
          }}>
            <Icon fontSize="small">event</Icon> Calendrier
          </MDBox>
        </MDBox>

        {loading ? (
          <MDBox p={4} textAlign="center"><MDTypography variant="body2">Chargement...</MDTypography></MDBox>
        ) : reunions.length === 0 ? (
          <Card sx={{ p: 4, textAlign: "center" }}>
            <Icon sx={{ fontSize: 48, color: "#bbb", mb: 1 }}>event_busy</Icon>
            <MDTypography variant="h6" color="text">Aucune réunion pour le moment</MDTypography>
            {isAdmin && (
            <MDTypography variant="body2" color="text" mb={2}>
              Cliquez sur « Nouvelle réunion » pour en créer une.
            </MDTypography>
            )}
          </Card>
        ) : viewMode === "liste" ? (
          <Grid container spacing={2}>
            {reunions.map((r) => (
              <Grid item xs={12} md={6} lg={4} key={r.id}>
                <Card sx={{ p: 2, cursor: "pointer", "&:hover": { boxShadow: 4 } }}
                  onClick={() => navigate(`/reunions/${r.id}`)}>
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <MDTypography variant="h6" color="dark" sx={{ flex: 1, mr: 1 }}>{r.titre}</MDTypography>
                    {statutBadge(r.statut)}
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Icon sx={{ fontSize: 16, color: "#1565c0" }}>event</Icon>
                    <MDTypography variant="caption" color="text">
                      {new Date(r.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                    </MDTypography>
                  </MDBox>
                  <MDBox display="flex" alignItems="center" gap={1} mb={0.5}>
                    <Icon sx={{ fontSize: 16, color: "#1565c0" }}>schedule</Icon>
                    <MDTypography variant="caption" color="text">
                      {r.heure_debut}{r.heure_fin ? ` — ${r.heure_fin}` : ""}
                    </MDTypography>
                  </MDBox>
                  {r.lieu && (
                    <MDBox display="flex" alignItems="center" gap={1}>
                      <Icon sx={{ fontSize: 16, color: "#1565c0" }}>place</Icon>
                      <MDTypography variant="caption" color="text">{r.lieu}</MDTypography>
                    </MDBox>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Card sx={{ p: 2 }}>
            <div style={{ height: 600 }}>
              <Calendar
                localizer={localizer}
                events={events}
                startAccessor="start"
                endAccessor="end"
                messages={messagesFr}
                culture="fr"
                views={["month", "week", "day", "agenda"]}
                onSelectEvent={(event) => navigate(`/reunions/${event.id}`)}
                eventPropGetter={(event) => {
                  const statut = event.resource.statut;
                  const bg = statut === "terminee" ? "#2e7d32" : statut === "annulee" ? "#c62828" : "#1565c0";
                  return { style: { backgroundColor: bg, borderRadius: 6, border: "none", fontSize: 12 } };
                }}
              />
            </div>
          </Card>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
